import { useEffect, useMemo, useState } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import {
  Button,
  ClickAwayListener,
  Stack,
  FormControl,
  Typography,
  useMediaQuery,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// component
import Iconify from '../../../components/iconify';
import { tokenService } from 'src/services';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateCustomerValue } from 'src/store/customer';
import api from 'src/services/BaseService';
import packageJson from '../../../../package.json';
import { toast } from 'react-toastify';

// ----------------------------------------------------------------------

const HEADER_MOBILE = 64;
const HEADER_DESKTOP = 92;

const StyledSearchbar = styled('div')(({ theme }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: HEADER_MOBILE,
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  [theme.breakpoints.up('md')]: {
    height: HEADER_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

// ----------------------------------------------------------------------_

export default function Searchbar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedCustomerStatus, setSelectedCustomerStatus] = useState(null);
  const [branchesAndMallsIds, setBranchesAndMallsIds] = useState([]);
  const isMobile = useMediaQuery('(max-width:768px)');

  const handleOpen = () => setOpen(!open);
  const handleClose = () => setOpen(false);

  const handleSelectChange = async (value) => {
    if (!value) return;

    const user = tokenService.getUser();
    const accountCategory = user?.accountCategory;

    if (accountCategory === 'mall-logistics' || accountCategory === 'mall') {
      localStorage.setItem('mallID', value.id);
      localStorage.setItem('branchID', '');
      localStorage.setItem('easypointID', value?.points?.malkabul?.id ?? '');
      dispatch(updateCustomerValue(value));
      setSelectedCustomerStatus(value);

      try {
        const res = await api.post(
          'flow/malls-get-default-transportation-tools',
          {
            mallID: value.id,
          }
        );
        if (res.data.status) {
          localStorage.setItem(
            'mallsTransportationTools',
            JSON.stringify(res.data.result)
          );
        } else {
          localStorage.setItem('mallsTransportationTools', '[]');
        }
      } catch {
        localStorage.setItem('mallsTransportationTools', '[]');
      } finally {
        window.location.reload();
      }
    } else if (accountCategory === 'company') {
      localStorage.setItem('mallID', value.id);
      localStorage.setItem('easypointID', value?.points?.malkabul?.id ?? '');

      const matchedBranch = branchesAndMallsIds.find(
        (item) => item.mallID === value.id
      );
      try {
        if (matchedBranch) {
          localStorage.setItem('branchID', matchedBranch.branchID);
          dispatch(
            updateCustomerValue({
              id: value.id,
              title: value.title,
              branchID: matchedBranch.branchID,
              mallTitle: null,
            })
          );
          setSelectedCustomerStatus(value);
        } else {
          toast.error("Seçtiğiniz AVM'de mağazanız bulunamadı.");
        }
      } finally {
        window.location.reload();
      }
    } else {
      // store
      localStorage.setItem('mallID', value.mallID);
      localStorage.setItem('branchID', value.branchID);
      localStorage.setItem(
        'easypointID',
        value?.mall?.[0]?.points?.malkabul?.id ?? ''
      );

      dispatch(
        updateCustomerValue({
          id: value.branchID,
          title: value.title,
          mallTitle: value.mall?.[0]?.title,
          branchID: value.branchID,
        })
      );
      setSelectedCustomerStatus({
        id: value.branchID,
        title: value.title,
        mallTitle: value.mall?.[0]?.title,
      });

      try {
        const res = await api.post(
          'flow/malls-get-default-transportation-tools',
          {
            mallID: value.mallID,
          }
        );
        if (res.data.status) {
          localStorage.setItem(
            'mallsTransportationTools',
            JSON.stringify(res.data.result)
          );
        } else {
          localStorage.setItem('mallsTransportationTools', '[]');
        }
      } catch {
        localStorage.setItem('mallsTransportationTools', '[]');
      } finally {
        window.location.reload();
      }
    }
  };

  const getOptionLabel = (opt) => {
    if (!opt) return '';
    if (tokenService.getUser()?.accountCategory === 'store') {
      const mallTitle = opt?.mall?.[0]?.title;
      return `${opt?.title ?? ''}${mallTitle ? ` (${mallTitle})` : ''}`;
    }
    return `${opt?.title ?? ''}`;
  };

  const options = useMemo(() => {
    const user = tokenService.getUser();
    const accountCategory = user?.accountCategory;

    if (accountCategory === 'store') {
      const branches = user?.authLocations?.branch;
      if (Array.isArray(branches) && branches.length > 0) {
        return [...branches].sort((a, b) =>
          (a?.title || '').localeCompare(b?.title || '')
        );
      }
      return branches ? [branches] : [];
    }

    const malls = user?.authLocations?.mall;
    if (Array.isArray(malls) && malls.length > 0) {
      return [...malls].sort((a, b) =>
        (a?.title || '').localeCompare(b?.title || '')
      );
    }
    return malls ? [malls] : [];
  }, []);

  useEffect(() => {
    let customer;
    const mallID = localStorage.getItem('mallID');
    const branchID = localStorage.getItem('branchID');
    const branchesAndMalls = [];

    if (tokenService.getUser()?.accountCategory === 'company') {
      customer = tokenService
        .getUser()
        ?.authLocations?.mall?.find((item) => item?.id === mallID);

      tokenService.getUser()?.authLocations?.branch?.forEach((item) => {
        branchesAndMalls.push({
          branchID: item.branchID,
          mallID: item.mallID,
        });
      });

      setBranchesAndMallsIds(branchesAndMalls);
    } else {
      if (tokenService.getUser()?.authLocations?.branch) {
        if (tokenService.getUser()?.authLocations?.branch?.length > 0) {
          customer = tokenService
            .getUser()
            ?.authLocations?.branch?.find((item) => item.branchID === branchID);
        } else {
          customer = tokenService.getUser()?.authLocations?.branch?.['0'];
        }
      } else {
        if (tokenService.getUser()?.authLocations?.mall?.length > 0) {
          customer = tokenService
            .getUser()
            ?.authLocations?.mall?.find((item) => item?.id === mallID);
        } else {
          customer = tokenService.getUser()?.authLocations?.mall?.['0'];
        }
      }
    }

    if (tokenService.getUser()?.accountCategory === 'store' && customer) {
      customer.mallTitle = customer.mall?.[0]?.title;
    }

    setSelectedCustomerStatus(customer || null);

    dispatch(
      updateCustomerValue({
        id:
          tokenService.getUser()?.accountCategory === 'store'
            ? customer?.branchID
            : customer?.id,
        title: customer?.title,
        mallTitle:
          tokenService.getUser()?.accountCategory === 'store'
            ? customer?.mall?.[0]?.title
            : null,
        branchID:
          tokenService.getUser()?.accountCategory === 'company' ? branchID : '',
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <Stack
          mr={1}
          direction="column"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <FormControl
            sx={{ flexDirection: 'row', height: 50, alignItems: 'center' }}
          >
            <Autocomplete
              sx={{ marginRight: '8px', minWidth: 250 }}
              options={options}
              value={selectedCustomerStatus}
              onChange={(_, newValue) => handleSelectChange(newValue)}
              autoHighlight
              clearOnEscape
              noOptionsText={'Veri bulunamadı.'}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={(option, value) => {
                const accountCategory = tokenService.getUser()?.accountCategory;
                if (accountCategory === 'store') {
                  return option?.branchID === value?.branchID;
                }
                return option?.id === value?.id;
              }}
              slotProps={{
                paper: { sx: { overflow: 'visible' } },
                listbox: { sx: { maxHeight: 48 * 4.5, overflow: 'auto' } },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="AVM / Mağaza seçiniz"
                />
              )}
            />

            {tokenService.getUser()?.accountCategory !== 'mall' &&
              tokenService.getUser()?.frontAuth?.includes('operations') && (
                <>
                  {isMobile ? null : (
                    <Button
                      variant="contained"
                      sx={{ height: '50px' }}
                      startIcon={<Iconify icon="eva:plus-fill" />}
                      onClick={() =>
                        navigate('/dashboard/finished-jobs', {
                          state: { showCreateModal: true },
                        })
                      }
                    >
                      İş Emri Oluştur
                    </Button>
                  )}
                </>
              )}

            {isMobile ? null : (
              <Typography sx={{ color: '#b5b5b5', marginLeft: '20px' }}>
                Versiyon: {packageJson.version}
              </Typography>
            )}
          </FormControl>
        </Stack>
      </div>
    </ClickAwayListener>
  );
}
